export const GRAPHICS_QUALITY = Object.freeze({
    MAXIMUM: 'high',
    ADAPTIVE: 'adaptive',
    MEDIUM: 'medium',
    MINIMUM: 'low',
})

export const GRAPHICS_QUALITY_OPTIONS = Object.freeze([
    {
        value: GRAPHICS_QUALITY.MAXIMUM,
        title: 'Максимум',
        shortTitle: 'MAX',
        description: 'Полное разрешение без автоматического снижения качества.',
        hint: 'Лучшее изображение. При тяжёлой сцене возможны просадки FPS.',
    },
    {
        value: GRAPHICS_QUALITY.ADAPTIVE,
        title: 'Высокое адаптивное',
        shortTitle: 'AUTO',
        description: 'Начинает с высокого качества и плавно снижает DPR только при нагрузке.',
        hint: 'Оптимальный режим для прогулки по большой сцене.',
    },
    {
        value: GRAPHICS_QUALITY.MEDIUM,
        title: 'Среднее',
        shortTitle: 'MID',
        description: 'Фиксированное умеренное разрешение без скачков качества.',
        hint: 'Стабильная работа на большинстве компьютеров и планшетов.',
    },
    {
        value: GRAPHICS_QUALITY.MINIMUM,
        title: 'Минимум',
        shortTitle: 'LOW',
        description: 'Сниженное разрешение, отключённые тени и сглаживание.',
        hint: 'Для слабых устройств и самых тяжёлых сцен.',
    },
])

const QUALITY_ALIASES = Object.freeze({
    max: GRAPHICS_QUALITY.MAXIMUM,
    maximum: GRAPHICS_QUALITY.MAXIMUM,
    ultra: GRAPHICS_QUALITY.MAXIMUM,
    adaptivehigh: GRAPHICS_QUALITY.ADAPTIVE,
    'adaptive-high': GRAPHICS_QUALITY.ADAPTIVE,
    auto: GRAPHICS_QUALITY.ADAPTIVE,
    normal: GRAPHICS_QUALITY.MEDIUM,
    minimal: GRAPHICS_QUALITY.MINIMUM,
    minimum: GRAPHICS_QUALITY.MINIMUM,
})

export const normalizeGraphicsQuality = (
    value,
    fallback = GRAPHICS_QUALITY.MAXIMUM
) => {
    const clean = String(value || '').trim().toLowerCase()
    const allowed = Object.values(GRAPHICS_QUALITY)

    if (allowed.includes(clean)) return clean
    if (QUALITY_ALIASES[clean]) return QUALITY_ALIASES[clean]

    const cleanFallback = String(fallback || '').trim().toLowerCase()
    return allowed.includes(cleanFallback)
        ? cleanFallback
        : GRAPHICS_QUALITY.MAXIMUM
}

export const getGraphicsQualityOption = (value) => {
    const normalized = normalizeGraphicsQuality(value)
    return GRAPHICS_QUALITY_OPTIONS.find((option) => option.value === normalized)
        || GRAPHICS_QUALITY_OPTIONS[0]
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

export const getSceneGraphicsProfile = ({
    quality,
    isTouch = false,
    pixelRatio = 1,
} = {}) => {
    const normalized = normalizeGraphicsQuality(quality)
    const safePixelRatio = clamp(Number(pixelRatio) || 1, 0.5, 3)

    if (normalized === GRAPHICS_QUALITY.MAXIMUM) {
        const dpr = isTouch
            ? Math.min(Math.max(safePixelRatio, 1), 1.5)
            : Math.min(Math.max(safePixelRatio, 1), 2)

        return {
            quality: normalized,
            dpr,
            maxDpr: dpr,
            minDpr: dpr,
            adaptiveDpr: false,
            antialias: true,
            shadowsAllowed: true,
            shadowMapSize: isTouch ? 1024 : 2048,
        }
    }

    if (normalized === GRAPHICS_QUALITY.ADAPTIVE) {
        const maxDpr = isTouch
            ? Math.min(Math.max(safePixelRatio, 1), 1.35)
            : Math.min(Math.max(safePixelRatio, 1), 1.75)

        return {
            quality: normalized,
            dpr: maxDpr,
            maxDpr,
            minDpr: isTouch ? 0.8 : 0.9,
            adaptiveDpr: true,
            antialias: true,
            shadowsAllowed: true,
            shadowMapSize: isTouch ? 1024 : 1024,
        }
    }

    if (normalized === GRAPHICS_QUALITY.MEDIUM) {
        const dpr = isTouch ? 0.85 : 1

        return {
            quality: normalized,
            dpr,
            maxDpr: dpr,
            minDpr: dpr,
            adaptiveDpr: false,
            antialias: true,
            shadowsAllowed: true,
            shadowMapSize: isTouch ? 512 : 1024,
        }
    }

    const dpr = isTouch ? 0.6 : 0.7

    return {
        quality: GRAPHICS_QUALITY.MINIMUM,
        dpr,
        maxDpr: dpr,
        minDpr: dpr,
        adaptiveDpr: false,
        antialias: false,
        shadowsAllowed: false,
        shadowMapSize: 512,
    }
}
